/**
 * VideoPostList Component
 * 
 * Main interface for browsing and managing video posts. This component provides:
 * - Grid view of all video posts with cards
 * - Search functionality
 * - Pagination support
 * - Create new video post functionality
 * - Quick actions menu (edit metadata, delete)
 * - Navigation to transcript management for each post
 * 
 * Used in: App.js route /video-posts
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Alert,
    Grid,
    TextField,
    Menu,
    MenuItem as MenuItemMui,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputAdornment,
    Pagination
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    Search
} from '@mui/icons-material';
import { userRequest } from '../../requestMethods';
import VideoPostMetadataEditor from './VideoPostMetadataEditor';
import VideoPostCard from './VideoPostCard';
import PageContainer from '../layout/PageContainer';

const VideoPostList = () => {
    const navigate = useNavigate();
    const [videoPosts, setVideoPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPost, setCurrentPost] = useState(null);
    const [mode, setMode] = useState('list'); // 'list', 'create', 'edit'
    const [alert, setAlert] = useState({ show: false, message: '', severity: 'info' });
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedPost, setSelectedPost] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const fetchVideoPosts = async () => {
        setLoading(true);
        try {
            const response = await userRequest.get('/contentVideoPost', {
                params: {
                    page,
                    limit: 12,
                    search: searchQuery || undefined
                }
            });

            setVideoPosts(response.data.videoPosts);
            setTotalPages(response.data.totalPages);
        } catch (error) {
            console.error('Error fetching video posts:', error);
            showAlert('Error fetching video posts: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVideoPosts();
    }, [page, searchQuery]);

    const showAlert = (message, severity = 'info') => {
        setAlert({ show: true, message, severity });
        setTimeout(() => setAlert({ show: false, message: '', severity: 'info' }), 6000);
    };

    const handleCardClick = (post) => {
        navigate(`/video-posts/edit/${post._id}`);
    };

    const handleMenuClick = (event, post) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedPost(post);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedPost(null);
    };

    const handleEditMetadata = () => {
        setCurrentPost(selectedPost);
        setMode('edit');
        handleMenuClose();
    };

    const handleDeleteClick = () => {
        setCurrentPost(selectedPost);
        setDeleteDialogOpen(true);
        handleMenuClose();
    };

    const handleDeleteConfirm = async () => {
        try {
            await userRequest.delete(`/contentVideoPost/${currentPost._id}`);
            showAlert('Video post deleted successfully', 'success');
            fetchVideoPosts();
        } catch (error) {
            console.error('Error deleting video post:', error);
            showAlert('Error deleting video post: ' + error.message, 'error');
        } finally {
            setDeleteDialogOpen(false);
            setSelectedPost(null);
        }
    };

    const handleVideoPostSaved = () => {
        setMode('list');
        setCurrentPost(null);
        fetchVideoPosts();
        showAlert('Video post saved successfully', 'success');
    };

    const handleCreateNew = () => {
        setCurrentPost(null);
        setMode('create');
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
        setPage(1); // Reset to first page when searching
    };

    if (mode === 'create' || mode === 'edit') {
        return (
            <VideoPostMetadataEditor
                post={currentPost}
                onSave={handleVideoPostSaved}
                onCancel={() => setMode('list')}
            />
        );
    }

    return (
        <PageContainer>
            <Box sx={{ mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                        Video Posts
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleCreateNew}
                        sx={{ borderRadius: 2 }}
                    >
                        Create Video Post
                    </Button>
                </Box>

                {/* Search Bar */}
                <TextField
                    fullWidth
                    placeholder="Search video posts by title, author, or tags..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 3 }}
                />

                {alert.show && (
                    <Alert severity={alert.severity} sx={{ mb: 2 }}>
                        {alert.message}
                    </Alert>
                )}
            </Box>

            {loading ? (
                <Typography>Loading video posts...</Typography>
            ) : (
                <>
                    {videoPosts.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                                {searchQuery ? 'No video posts found matching your search.' : 'No video posts yet.'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {searchQuery 
                                    ? 'Try adjusting your search terms or create a new video post.'
                                    : 'Create your first video post to get started.'
                                }
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={handleCreateNew}
                                sx={{ borderRadius: 2 }}
                            >
                                Create First Video Post
                            </Button>
                        </Box>
                    ) : (
                        <>
                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                {videoPosts.map((post) => (
                                    <Grid item xs={12} sm={6} md={4} key={post._id}>
                                        <VideoPostCard
                                            post={post}
                                            onCardClick={handleCardClick}
                                            onMenuClick={handleMenuClick}
                                        />
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <Grid container justifyContent="center" sx={{ mt: 3 }}>
                                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                                        <Pagination
                                            count={totalPages}
                                            page={page}
                                            onChange={(event, value) => {
                                                setPage(value);
                                            }}
                                            color="primary"
                                            size="large"
                                            showFirstButton
                                            showLastButton
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                            Page {page} of {totalPages}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                        </>
                    )}
                </>
            )}

            {/* Context Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: { minWidth: 120 }
                }}
            >
                <MenuItemMui onClick={handleEditMetadata}>
                    <Edit fontSize="small" sx={{ mr: 1 }} />
                    Edit Metadata
                </MenuItemMui>
                <MenuItemMui onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                    <Delete fontSize="small" sx={{ mr: 1 }} />
                    Delete
                </MenuItemMui>
            </Menu>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Video Post</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "{currentPost?.title}"? 
                        This will also delete all associated transcripts and translations. 
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
};

export default VideoPostList; 